/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework from '@greenville/framework';
import utils from '../../../../common/utils';
import en from '../../../../common/languages/en';

const REQUEST_TIMEOUT_MESSAGE = 'timeout of 60000ms exceeded';
const REQUEST_TIMEOUT_USER_MESSAGE = 'Request timeout. Please try again.';

export function resolveLangMessage(key) {
  if (!key || typeof key !== 'string') {
    return key ?? '';
  }
  return en[key] ?? key;
}

export function getErrorMessage(data, fallback) {
  return data?.error || data?.message || resolveLangMessage(fallback) || fallback;
}

export function setNotification({ type, message, langMessage }) {
  Framework.getStoreRegistry()
    .getStore('notificationState')
    .set({
      open: true,
      type,
      message: message || '',
      langMessage: langMessage || ''
    });
}

export function clearGraphModelError() {
  Framework.getStoreRegistry().getStore('graphModel').clearError();
}

export function setGraphModelError(message) {
  Framework.getStoreRegistry().getStore('graphModel').setError(message);
}

export function buildVersionQuery(version) {
  return version != null && version !== '' ? `?version=${encodeURIComponent(String(version))}` : '';
}

export function encodeGraphPathSegment(value) {
  return encodeURIComponent(String(value));
}

/** Path for eval APIs keyed by rubricId (no version). Optional suffix e.g. rubrictext, scheduler. */
export function buildEvalByRubricIdPath(graphId, rubricId, suffix = '') {
  const base = `/graphs/${encodeGraphPathSegment(graphId)}/eval/${encodeGraphPathSegment(rubricId)}`;
  return suffix ? `${base}/${suffix}` : base;
}

/** Stateless eval path (no rubricId) e.g. rubrictext/enhance before first save. */
export function buildEvalStatelessPath(graphId, suffix) {
  return `/graphs/${encodeGraphPathSegment(graphId)}/eval/${suffix}`;
}

export function unwrapServiceData(response) {
  const payload = response?.data;
  if (payload == null) {
    return null;
  }
  if (payload.data != null && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    return payload.data;
  }
  return payload;
}

export function isRequestTimeout(error) {
  return error?.message === REQUEST_TIMEOUT_MESSAGE || error?.code === 'ECONNABORTED';
}

export function publishErrorEvent(errorEvent, payload) {
  if (errorEvent) {
    Framework.getEventManager().publish(errorEvent, payload);
  }
}

/**
 * Graph Tester Eval Criteria errors — updates graphTesterModel + notification + event.
 */
export function handleGraphTesterEvalError(error, { fallbackLangMessage, errorEvent }) {
  const graphTesterModel = Framework.getStoreRegistry().getStore('graphTesterModel');
  const fallback = resolveLangMessage(fallbackLangMessage);
  const { response } = error || {};

  if (response) {
    const { status, data } = response;
    if (status === 401) {
      utils.redirectToLogin();
    }
    const msg = getErrorMessage(data, fallbackLangMessage);
    graphTesterModel?.setEvaluationCriteriaError(msg);
    setNotification({ type: 'error', message: msg });
    publishErrorEvent(errorEvent, { data, message: msg });
    return;
  }

  const msg = error?.message || fallback;
  graphTesterModel?.setEvaluationCriteriaError(msg);
  setNotification({ type: 'error', message: msg });
  publishErrorEvent(errorEvent, { message: msg });
}

/**
 * Shared HTTP error handling for graph BaseService implementations.
 */
export function handleGraphServiceHttpError(error, { fallbackLangMessage, errorEvent, updateGraphModelError = true }) {
  const { response } = error;
  const fallback = resolveLangMessage(fallbackLangMessage);

  if (response) {
    const { status, data } = response;
    if (status === 401) {
      utils.redirectToLogin();
    }
    const msg = getErrorMessage(data, fallbackLangMessage);
    setNotification({ type: 'error', message: msg });
    if (updateGraphModelError) {
      setGraphModelError(msg);
    }
    publishErrorEvent(errorEvent, { data, message: msg });
    return;
  }

  if (isRequestTimeout(error)) {
    const msg = REQUEST_TIMEOUT_USER_MESSAGE;
    setNotification({ type: 'error', message: msg });
    if (updateGraphModelError) {
      setGraphModelError(msg);
    }
    publishErrorEvent(errorEvent, { message: msg });
    return;
  }

  const msg = error?.message || fallback;
  setNotification({ type: 'error', message: msg });
  if (updateGraphModelError) {
    setGraphModelError(msg);
  }
  publishErrorEvent(errorEvent, { message: msg });
}
